import { useCallback, useRef } from "react";
import { UrgentVisitStep } from "../../state/urgent-visit";
import {
  useUrgentVisitStore,
} from "../redux-hooks/urgent-visit-store/urgent-visit.store";

export const useUrgentVisitNavigation = (
  onCloseModal?: () => void,
) => {
  const {
    currentStep, selectedPets, currentPetIndex, isMultiPetOwner,
    setStep, setCurrentPetIndex, rescheduleAppointmentId, petAppointmentMap,
  } = useUrgentVisitStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  const isSinglePetOwner = !isMultiPetOwner;
  const isGroupedReschedule = Object.keys(petAppointmentMap).length > 1;
  const isSinglePetFlow = isSinglePetOwner || (!!rescheduleAppointmentId && !isGroupedReschedule);
  const isLastPet = currentPetIndex >= selectedPets.length - 1;

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const goToStep = useCallback(
    (step: UrgentVisitStep) => {
      setStep(step);
      scrollToTop();
    },
    [setStep, scrollToTop],
  );

  const goToNextStep = useCallback(() => {
    switch (currentStep) {
    case UrgentVisitStep.PET_SELECTION:
      goToStep(UrgentVisitStep.TIME_SELECTION);
      break;
    case UrgentVisitStep.TIME_SELECTION:
      goToStep(UrgentVisitStep.REASON_SELECTION);
      break;
    case UrgentVisitStep.REASON_SELECTION:
      goToStep(UrgentVisitStep.ADDITIONAL_NOTES);
      break;
    case UrgentVisitStep.ADDITIONAL_NOTES:
      if (isLastPet) {
        goToStep(UrgentVisitStep.REVIEW);
      } else {
        setCurrentPetIndex(currentPetIndex + 1);
        goToStep(UrgentVisitStep.REASON_SELECTION);
      }
      break;
    case UrgentVisitStep.REVIEW:
      goToStep(UrgentVisitStep.BOOKED);
      break;
    default:
      break;
    }
  }, [currentStep, isLastPet, currentPetIndex, setCurrentPetIndex, goToStep]);

  const goToPreviousStep = useCallback(() => {
    switch (currentStep) {
    case UrgentVisitStep.PET_SELECTION:
      onCloseModal?.();
      break;
    case UrgentVisitStep.TIME_SELECTION:
      if (isSinglePetFlow) {
        onCloseModal?.();
      } else {
        goToStep(UrgentVisitStep.PET_SELECTION);
      }
      break;
    case UrgentVisitStep.REASON_SELECTION:
      if (currentPetIndex === 0) {
        goToStep(UrgentVisitStep.TIME_SELECTION);
      } else {
        setCurrentPetIndex(currentPetIndex - 1);
        goToStep(UrgentVisitStep.ADDITIONAL_NOTES);
      }
      break;
    case UrgentVisitStep.ADDITIONAL_NOTES:
      goToStep(UrgentVisitStep.REASON_SELECTION);
      break;
    case UrgentVisitStep.REVIEW:
      setCurrentPetIndex(selectedPets.length - 1);
      goToStep(UrgentVisitStep.ADDITIONAL_NOTES);
      break;
    default:
      break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentPetIndex, selectedPets.length, isSinglePetFlow, setCurrentPetIndex, goToStep, onCloseModal]);

  const firstStep = isSinglePetFlow ? UrgentVisitStep.TIME_SELECTION : UrgentVisitStep.PET_SELECTION;
  const canGoBack = currentStep !== firstStep && currentStep !== UrgentVisitStep.BOOKED;

  const showCloseButton = currentStep !== UrgentVisitStep.BOOKED;

  return {
    goToNextStep,
    goToPreviousStep,
    goToStep,
    canGoBack,
    showCloseButton,
    scrollRef,
  };
};
